import { Quote } from "lucide-react";
import PageHero from "@/components/PageHero";
import CTASection from "@/components/CTASection";
import { Reveal } from "@/components/motion";
import usePageMeta from "@/hooks/usePageMeta";
import useTestimonials from "@/hooks/useTestimonials";
import { BRAND, CASE_STUDIES } from "@/data/content";

export default function Testimonials() {
  usePageMeta(
    `Testimonials & Case Studies — ${BRAND.name}`,
    "What clients say about working with a 40-year trade veteran turned AI strategist — plus real case studies: challenge, solution, result."
  );
  const testimonials = useTestimonials();

  return (
    <div data-testid="testimonials-page">
      <PageHero
        kicker="Testimonials & Case Studies"
        lines={["Results That", "Speak for Themselves."]}
        description="Kind words from clients, and the stories behind them — real challenges, practical solutions, measurable results."
        accent="gold"
      />

      {/* CASE STUDIES */}
      <section className="py-20 md:py-28" data-testid="case-studies-section">
        <div className="mx-auto max-w-6xl px-6">
          <Reveal className="mb-14 max-w-2xl">
            <p className="kicker mb-5 text-gold">Case Studies</p>
            <h2 className="font-serif text-3xl leading-tight text-white md:text-4xl">
              Challenge. Solution. <span className="italic text-gold">Result.</span>
            </h2>
          </Reveal>
          <div className="grid gap-6 md:grid-cols-3">
            {CASE_STUDIES.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.12} className="h-full">
                <div className="flex h-full flex-col rounded-2xl border border-[#1E3A5F] bg-[#0A2540] p-8 transition-[transform,border-color] duration-500 hover:-translate-y-2 hover:border-gold/50">
                  <span className="font-serif text-5xl text-gold/25">0{i + 1}</span>
                  <h3 className="mt-4 font-serif text-2xl text-white">{c.title}</h3>
                  <div className="mt-6 space-y-5 text-sm leading-relaxed">
                    <div>
                      <p className="kicker mb-2 !text-[10px] text-slate-500">Challenge</p>
                      <p className="text-slate-400">{c.challenge}</p>
                    </div>
                    <div>
                      <p className="kicker mb-2 !text-[10px] text-slate-500">Solution</p>
                      <p className="text-slate-400">{c.solution}</p>
                    </div>
                    <div>
                      <p className="kicker mb-2 !text-[10px] text-gold">Result</p>
                      <p className="text-slate-200">{c.result}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* QUOTES GRID */}
      <section className="py-20 md:py-28" data-testid="quotes-section">
        <div className="mx-auto max-w-6xl px-6">
          <Reveal className="mb-14 max-w-2xl">
            <p className="kicker mb-5 text-gold">Client Words</p>
            <h2 className="font-serif text-3xl leading-tight text-white md:text-4xl">
              In Their <span className="italic text-gold">Own Words.</span>
            </h2>
          </Reveal>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {testimonials.map((t, i) => (
              <Reveal key={i} delay={(i % 3) * 0.1} className="h-full">
                <figure className="flex h-full flex-col rounded-2xl border border-[#1E3A5F] bg-[#0A2540] p-8">
                  <Quote className="h-6 w-6 text-gold/50" strokeWidth={1.5} />
                  <blockquote className="mt-5 flex-1 font-serif text-lg italic leading-relaxed text-slate-200">
                    "{t.quote}"
                  </blockquote>
                  <figcaption className="mt-6 border-t border-[#1E3A5F] pt-5">
                    <p className="text-sm font-semibold text-white">{t.name}</p>
                    <p className="mt-1 text-xs uppercase tracking-[0.2em] text-slate-500">{t.role}</p>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection
        kicker="Your Story Next"
        title="Ready to become the next success story?"
        subtitle="Every result above started with a single conversation. Yours can too."
      />
    </div>
  );
}
